import React from "react";
import { Box } from "@mui/material";
import { marketingKeyframes, glowOrb, accentColors } from "./marketingStyles";
import { marketingCtaPhotos } from "./marketingCtaConfig";

const photoEdgeFade = (index, total) => {
  if (total <= 1) return "transparent";
  if (index === 0) {
    return "linear-gradient(to right, transparent 50%, rgba(10, 10, 10, 0.5) 100%)";
  }
  return "linear-gradient(to left, transparent 50%, rgba(10, 10, 10, 0.5) 100%)";
};

/** Soft vertical fade so section edges blend with page background. */
const sectionVerticalFade = `
  linear-gradient(
    to bottom,
    rgba(10, 10, 10, 0.3) 0%,
    transparent 12%,
    transparent 88%,
    rgba(10, 10, 10, 0.3) 100%
  )
`;

/** Localized center scrim — keeps copy readable without crushing player photos. */
const centerTextScrim = `
  radial-gradient(
    ellipse 65% 75% at 50% 50%,
    rgba(10, 10, 10, 0.78) 0%,
    rgba(10, 10, 10, 0.42) 50%,
    transparent 72%
  )
`;

export const MarketingVsBadge = ({ sx, ...rest }) => (
  <Box
    aria-hidden
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: { xs: 64, sm: 80 },
      height: { xs: 36, sm: 44 },
      px: { xs: 2, sm: 2.5 },
      borderRadius: 999,
      bgcolor: "rgba(10, 10, 10, 0.92)",
      border: `2px solid ${accentColors[0]}`,
      color: "#ffffff",
      fontWeight: 800,
      fontSize: { xs: "0.9rem", sm: "1.05rem" },
      letterSpacing: "0.12em",
      boxShadow: `0 0 24px ${accentColors[0]}66, 0 4px 16px rgba(0, 0, 0, 0.55)`,
      ...sx,
    }}
    {...rest}
  >
    VS
  </Box>
);

/**
 * Split-screen CTA background: two rival photos flank centered copy.
 * Stacks vertically on narrow viewports.
 */
const MarketingCtaBackground = ({
  children,
  photos = marketingCtaPhotos,
  sx,
  ...rest
}) => (
  <Box
    sx={{
      ...marketingKeyframes,
      position: "relative",
      overflow: "hidden",
      py: { xs: 7, md: 9 },
      borderTop: 1,
      borderColor: "divider",
      bgcolor: "background.default",
      ...sx,
    }}
    {...rest}
  >
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        pointerEvents: "none",
      }}
    >
      {photos.map((photo, index) => (
        <Box
          key={photo.src}
          sx={{
            position: "relative",
            flex: 1,
            minHeight: { xs: "50%", sm: "100%" },
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={photo.src}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: photo.objectPosition ?? "center",
              filter: "brightness(1.08) contrast(1.06) saturate(1.05)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: photoEdgeFade(index, photos.length),
            }}
          />
        </Box>
      ))}
    </Box>

    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        background: `${centerTextScrim}, ${sectionVerticalFade}`,
        pointerEvents: "none",
      }}
    />

    <Box sx={{ ...glowOrb(accentColors[0], 200), top: -60, left: -40, opacity: 0.45 }} />
    <Box sx={{ ...glowOrb(accentColors[2], 200), bottom: -60, right: -40, opacity: 0.45 }} />

    <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
  </Box>
);

export default MarketingCtaBackground;
